import { useRef, useState } from "react";
import { Network, Plus, Trash2, FileDown, Users, Columns3, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const uid = () => Date.now() + "-" + Math.random().toString(36).slice(2, 7);
const labelOf = (n) => (n ? (n.naam || n.functie || n.afdeling || "(naamloos)") : "");

export default function OrganogramPaneel({ organogram, setOrganogram, canEdit }) {
  const nodes = (organogram && Array.isArray(organogram.nodes)) ? organogram.nodes : [];
  const view = (organogram && organogram.view) || "boom";
  const [beheer, setBeheer] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const chartRef = useRef(null);

  const setNodes = (fn) => setOrganogram((o) => ({ ...(o || {}), nodes: typeof fn === "function" ? fn((o && o.nodes) || []) : fn }));
  const setView = (v) => setOrganogram((o) => ({ ...(o || {}), view: v }));
  const addNode = () => setNodes((arr) => [...arr, { id: uid(), naam: "", functie: "", afdeling: "", parentId: "", rol: "persoon", aantal: "", adviesAan: [] }]);
  const updNode = (id, f, v) => setNodes((arr) => arr.map((n) => (n.id === id ? { ...n, [f]: v } : n)));
  const remNode = (id) => { if (!window.confirm("Dit blok uit het organogram verwijderen?")) return; setNodes((arr) => arr.filter((n) => n.id !== id).map((n) => ({ ...n, parentId: n.parentId === id ? "" : n.parentId, adviesAan: (n.adviesAan || []).filter((x) => x !== id) }))); };
  const toggleAdvies = (id, targetId) => setNodes((arr) => arr.map((n) => {
    if (n.id !== id) return n;
    const cur = n.adviesAan || [];
    return { ...n, adviesAan: cur.includes(targetId) ? cur.filter((x) => x !== targetId) : [...cur, targetId] };
  }));
  const moveNode = (id, dir) => setNodes((arr) => {
    const node = arr.find((n) => n.id === id);
    if (!node) return arr;
    const pid = node.parentId || "";
    const sibs = arr.filter((n) => n.rol !== "adviseur" && (n.parentId || "") === pid);
    const si = sibs.findIndex((n) => n.id === id);
    const ti = si + dir;
    if (ti < 0 || ti >= sibs.length) return arr;
    const copy = arr.slice();
    const aIdx = copy.indexOf(node);
    const bIdx = copy.indexOf(sibs[ti]);
    [copy[aIdx], copy[bIdx]] = [copy[bIdx], copy[aIdx]];
    return copy;
  });

  const hierNodes = nodes.filter((n) => n.rol !== "adviseur");
  const advisors = nodes.filter((n) => n.rol === "adviseur");
  const childrenOf = (pid) => hierNodes.filter((n) => (n.parentId || "") === (pid || ""));
  const roots = hierNodes.filter((n) => !n.parentId || !hierNodes.some((m) => m.id === n.parentId));
  const afdelingen = [...new Set(hierNodes.map((n) => (n.afdeling || "").trim() || "Overig"))];

  const Card = ({ n, showMove }) => {
    const team = n.rol === "team";
    const hoofd = n.naam || (team ? (n.functie || "Team") : "");
    return (
      <div className={`org-card group ${team ? "org-team" : ""}`} data-testid="org-card">
        <div className="org-accent" />
        <div className="org-naam">{hoofd || <span className="org-leeg">Naam…</span>}</div>
        {n.functie && !(team && !n.naam) ? <div className="org-functie">{n.functie}</div> : null}
        {team && n.aantal ? <div className="org-aantal" data-testid="org-aantal-badge"><Users className="inline h-3 w-3 mr-1 -mt-0.5" />{n.aantal} medewerkers</div> : null}
        {n.afdeling ? <div className="org-tag">{n.afdeling}</div> : null}
        {n.rol !== "adviseur" && (n.adviesAan || []).length > 0 ? <div className="org-advies-note" data-testid="org-advies-note">⇢ adviseert: {(n.adviesAan || []).map((id) => labelOf(nodes.find((x) => x.id === id))).filter(Boolean).join(", ")}</div> : null}
        {canEdit && showMove && (
          <div className="org-move hidden group-hover:flex items-center justify-center gap-1 mt-2">
            <button data-testid="org-card-left" onClick={() => moveNode(n.id, -1)} className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-slate-500 hover:bg-slate-100" title="Naar links schuiven"><ChevronLeft className="h-3.5 w-3.5" /></button>
            <button data-testid="org-card-right" onClick={() => moveNode(n.id, 1)} className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-slate-500 hover:bg-slate-100" title="Naar rechts schuiven"><ChevronRight className="h-3.5 w-3.5" /></button>
          </div>
        )}
      </div>
    );
  };

  const renderTree = (n) => (
    <li key={n.id}>
      <Card n={n} showMove />
      {childrenOf(n.id).length > 0 && <ul>{childrenOf(n.id).map(renderTree)}</ul>}
    </li>
  );

  const StafStrook = () => (
    advisors.length > 0 ? (
      <div className="mb-6" data-testid="org-staf">
        <div className="mb-2 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-400">Staf &amp; advies</div>
        <div className="flex flex-wrap justify-center gap-3">
          {advisors.map((a) => (
            <div key={a.id} className="org-adviseur" data-testid="org-adviseur-card">
              <div className="org-naam">{a.naam || <span className="org-leeg">Naam…</span>}</div>
              {a.functie ? <div className="org-functie">{a.functie}</div> : null}
              <div className="org-advies-aan">adviseert: {(a.adviesAan || []).map((id) => labelOf(nodes.find((x) => x.id === id))).filter(Boolean).join(", ") || "n.v.t."}</div>
            </div>
          ))}
        </div>
      </div>
    ) : null
  );

  const downloadPdf = async () => {
    const el = chartRef.current;
    if (!el || !nodes.length) { toast.error("Voeg eerst blokken toe aan het organogram."); return; }
    setPdfBusy(true);
    try {
      const canvas = await html2canvas(el, { scale: 2, backgroundColor: "#ffffff", windowWidth: el.scrollWidth + 80 });
      const pdf = new jsPDF("l", "mm", "a4");
      const pw = 297, ph = 210, margin = 12;
      pdf.setFontSize(16); pdf.setTextColor(28, 100, 176);
      pdf.text("Organogram — Clazing Convenience Food B.V.", margin, 15);
      pdf.setFontSize(9); pdf.setTextColor(120, 120, 120);
      pdf.text(new Date().toLocaleDateString("nl-NL"), pw - margin, 15, { align: "right" });
      pdf.setDrawColor(28, 100, 176); pdf.setLineWidth(0.5); pdf.line(margin, 18, pw - margin, 18);
      const top = 24;
      const maxW = pw - 2 * margin, maxH = ph - top - margin;
      let iw = maxW, ih = canvas.height * (iw / canvas.width);
      if (ih > maxH) { ih = maxH; iw = canvas.width * (ih / canvas.height); }
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", margin + (maxW - iw) / 2, top, iw, ih);
      pdf.save("Organogram_Clazing.pdf");
      toast.success("Organogram-PDF gedownload.");
    } catch (e) { toast.error("PDF maken mislukt: " + (e.message || e)); }
    finally { setPdfBusy(false); }
  };

  return (
    <div className="mt-8 rounded-xl border border-slate-200 bg-white p-5" data-testid="organogram-paneel">
      <style>{`
        .orgtree, .orgtree ul { display:flex; padding-top:22px; position:relative; margin:0; justify-content:center; }
        .orgtree { padding:0; }
        .orgtree li { list-style:none; position:relative; padding:22px 10px 0; text-align:center; }
        .orgtree li::before, .orgtree li::after { content:''; position:absolute; top:0; right:50%; border-top:2px solid #cbd5e1; width:50%; height:22px; }
        .orgtree li::after { right:auto; left:50%; border-left:2px solid #cbd5e1; }
        .orgtree li:only-child::after, .orgtree li:only-child::before { display:none; }
        .orgtree li:only-child { padding-top:0; }
        .orgtree li:first-child::before, .orgtree li:last-child::after { border:0 none; }
        .orgtree li:last-child::before { border-right:2px solid #cbd5e1; border-radius:0 7px 0 0; }
        .orgtree li:first-child::after { border-radius:7px 0 0 0; }
        .orgtree ul::before { content:''; position:absolute; top:0; left:50%; border-left:2px solid #cbd5e1; width:0; height:22px; }
        .org-card { display:inline-block; position:relative; min-width:150px; max-width:210px; background:#fff; border:1.5px solid #cbd5e1; border-radius:10px; padding:10px 12px 10px; box-shadow:0 1px 3px rgba(15,23,42,.08); overflow:hidden; }
        .org-card.org-team { background:#f8fafc; border-color:#94a3b8; }
        .org-card.org-team .org-accent { background:#64748b; }
        .org-accent { position:absolute; top:0; left:0; right:0; height:5px; background:#1C64B0; }
        .org-naam { font-weight:800; font-size:13px; color:#0f172a; margin-top:4px; line-height:1.25; }
        .org-functie { font-size:11.5px; color:#475569; margin-top:2px; }
        .org-aantal { display:inline-block; margin-top:6px; font-size:10.5px; font-weight:700; color:#475569; background:#e2e8f0; border-radius:999px; padding:2px 8px; }
        .org-tag { display:inline-block; margin-top:6px; font-size:10px; font-weight:700; color:#1C64B0; background:#e8f0fb; border-radius:999px; padding:2px 8px; }
        .org-leeg { color:#94a3b8; font-weight:500; }
        .org-adviseur { display:inline-block; position:relative; min-width:160px; max-width:230px; background:#fafafa; border:2px dashed #94a3b8; border-radius:10px; padding:10px 12px; text-align:center; }
        .org-advies-aan { margin-top:6px; font-size:10.5px; color:#64748b; font-style:italic; line-height:1.4; }
        .org-advies-note { margin-top:7px; padding-top:6px; border-top:1px dashed #cbd5e1; font-size:10px; color:#64748b; font-style:italic; line-height:1.35; }
      `}</style>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#1C64B0]/10 text-[#1C64B0]"><Network className="h-5 w-5" /></div>
          <div>
            <div className="text-base font-bold text-slate-900">Organogram</div>
            <div className="text-xs text-slate-500">Personen, teams en adviseurs — schuif blokjes met de ◀ ▶ pijlen. Voor de managementrapportage of los als PDF.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex overflow-hidden rounded-md border border-slate-300">
            <button data-testid="org-view-boom" onClick={() => setView("boom")} className={`flex items-center gap-1 px-3 py-1.5 text-sm font-medium transition-colors ${view === "boom" ? "bg-[#1C64B0] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}><Network className="h-4 w-4" /> Boom</button>
            <button data-testid="org-view-afdeling" onClick={() => setView("afdeling")} className={`flex items-center gap-1 px-3 py-1.5 text-sm font-medium transition-colors ${view === "afdeling" ? "bg-[#1C64B0] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}><Columns3 className="h-4 w-4" /> Afdelingen</button>
          </div>
          {canEdit && <Button data-testid="org-beheer-btn" size="sm" variant={beheer ? "default" : "outline"} onClick={() => setBeheer((v) => !v)}><Users className="h-4 w-4 mr-1" /> Blokken beheren</Button>}
          <Button data-testid="org-pdf-btn" size="sm" className="bg-[#1C64B0] hover:bg-[#154E8A]" disabled={pdfBusy} onClick={downloadPdf}>{pdfBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileDown className="h-4 w-4 mr-1" />} PDF</Button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-100 bg-slate-50/40 p-5">
        {nodes.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-400" data-testid="org-leeg">Nog geen organogram. {canEdit ? "Klik op \"Blokken beheren\" om personen, teams of adviseurs toe te voegen." : ""}</div>
        ) : (
          <div ref={chartRef} data-testid="org-chart" className="inline-block min-w-full bg-white p-4" style={{ minWidth: "max-content" }}>
            <StafStrook />
            {view === "boom" ? (
              <ul className="orgtree">{roots.map(renderTree)}</ul>
            ) : (
              <div className="flex flex-wrap gap-5 justify-center">
                {afdelingen.map((af) => (
                  <div key={af} className="min-w-[180px]">
                    <div className="mb-3 rounded-md bg-[#1C64B0] px-3 py-1.5 text-center text-sm font-bold text-white">{af}</div>
                    <div className="space-y-3 flex flex-col items-center">
                      {hierNodes.filter((n) => ((n.afdeling || "").trim() || "Overig") === af).map((n) => <Card key={n.id} n={n} showMove={false} />)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {canEdit && beheer && (
        <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4" data-testid="org-beheer">
          <div className="mb-3 flex items-center justify-between">
            <div className="text-sm font-bold text-slate-800">Blokken</div>
            <Button data-testid="org-add-btn" size="sm" variant="outline" onClick={addNode}><Plus className="h-4 w-4 mr-1" /> Blok toevoegen</Button>
          </div>
          <div className="space-y-2">
            <div className="hidden md:grid grid-cols-[110px_1.2fr_1.2fr_1fr_1.2fr_auto] gap-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              <div>Rol</div><div>Naam</div><div>Functie</div><div>Afdeling</div><div>Valt onder</div><div></div>
            </div>
            {nodes.map((n) => (
              <div key={n.id} className="rounded-md border border-slate-100 bg-slate-50/50 p-2" data-testid="org-row">
                <div className="grid grid-cols-1 md:grid-cols-[110px_1.2fr_1.2fr_1fr_1.2fr_auto] gap-2">
                  <select data-testid="org-rol-select" value={n.rol || "persoon"} onChange={(e) => updNode(n.id, "rol", e.target.value)} className="h-9 rounded-md border border-slate-300 px-2 text-sm">
                    <option value="persoon">Persoon</option>
                    <option value="team">Team/groep</option>
                    <option value="adviseur">Adviseur</option>
                  </select>
                  <Input data-testid="org-naam-input" value={n.naam} onChange={(e) => updNode(n.id, "naam", e.target.value)} placeholder={n.rol === "team" ? "Teamnaam (optioneel)" : "Naam"} className="h-9" />
                  <Input data-testid="org-functie-input" value={n.functie} onChange={(e) => updNode(n.id, "functie", e.target.value)} placeholder={n.rol === "team" ? "bijv. Productiemedewerkers" : "Functie"} className="h-9" />
                  <Input data-testid="org-afdeling-input" value={n.afdeling} onChange={(e) => updNode(n.id, "afdeling", e.target.value)} placeholder="Afdeling" className="h-9" />
                  {n.rol === "adviseur" ? (
                    <div className="flex h-9 items-center px-2 text-[11px] italic text-slate-400" data-testid="org-parent-na">Niet in de lijn — zie "Adviseert aan"</div>
                  ) : (
                    <select data-testid="org-parent-select" value={n.parentId || ""} onChange={(e) => updNode(n.id, "parentId", e.target.value)} className="h-9 rounded-md border border-slate-300 px-2 text-sm">
                      <option value="">— bovenaan (geen) —</option>
                      {nodes.filter((m) => m.id !== n.id && m.rol !== "adviseur").map((m) => <option key={m.id} value={m.id}>{labelOf(m)}{m.functie && m.naam ? ` · ${m.functie}` : ""}</option>)}
                    </select>
                  )}
                  <div className="flex items-center gap-1">
                    {n.rol !== "adviseur" && <>
                      <button data-testid="org-move-left" onClick={() => moveNode(n.id, -1)} className="flex items-center justify-center rounded-md border border-slate-200 px-1.5 text-slate-500 hover:bg-slate-100" title="Naar links tussen gelijken"><ChevronLeft className="h-4 w-4" /></button>
                      <button data-testid="org-move-right" onClick={() => moveNode(n.id, 1)} className="flex items-center justify-center rounded-md border border-slate-200 px-1.5 text-slate-500 hover:bg-slate-100" title="Naar rechts tussen gelijken"><ChevronRight className="h-4 w-4" /></button>
                    </>}
                    <button data-testid="org-del-btn" onClick={() => remNode(n.id)} className="flex items-center justify-center rounded-md border border-rose-200 px-2 text-rose-500 hover:bg-rose-50" title="Verwijderen"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>

                {n.rol === "team" && (
                  <div className="mt-2 flex items-center gap-2 pl-1">
                    <span className="text-[11px] text-slate-500">Aantal medewerkers (optioneel):</span>
                    <Input type="number" min="0" data-testid="org-aantal-input" value={n.aantal || ""} onChange={(e) => updNode(n.id, "aantal", e.target.value)} className="h-8 w-28" placeholder="bijv. 6" />
                  </div>
                )}
                {(
                  <div className="mt-2 pl-1">
                    <div className="mb-1 text-[11px] text-slate-500">{n.rol === "adviseur" ? "Adviseert aan (klik om te kiezen — meerdere mogelijk):" : "Adviseert ook aan (optioneel — stippellijn-notitie in het blok):"}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {nodes.filter((m) => m.id !== n.id && m.rol !== "adviseur").map((m) => {
                        const on = (n.adviesAan || []).includes(m.id);
                        return <button key={m.id} data-testid="org-advies-chip" onClick={() => toggleAdvies(n.id, m.id)} className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${on ? "border-[#1C64B0] bg-[#1C64B0] text-white" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}>{labelOf(m)}</button>;
                      })}
                      {nodes.filter((m) => m.id !== n.id && m.rol !== "adviseur").length === 0 && <span className="text-[11px] text-slate-400">Voeg eerst andere personen/teams toe.</span>}
                    </div>
                  </div>
                )}
              </div>
            ))}
            {nodes.length === 0 && <p className="text-sm text-slate-400">Nog geen blokken. Klik op "Blok toevoegen".</p>}
          </div>
          <p className="mt-3 text-[11px] text-slate-400">Tip: gebruik <b>Team/groep</b> voor een hele ploeg (bijv. "Productiemedewerkers" + aantal) i.p.v. alle losse namen. Een <b>Adviseur</b> hoef je niet in de lijn te hangen — kies gewoon aan welke afdelingen hij advies geeft; hij verschijnt met stippellijn in de strook "Staf &amp; advies". Heeft iemand een lijnfunctie <b>én</b> een adviesrol (bijv. een manager die ook TD adviseert)? Houd die persoon gewoon als Persoon-blok en vul "Adviseert ook aan" in — er komt dan een stippellijn-notitie in zijn eigen blok. Met de ◀ ▶ pijlen schuif je gelijken naar links/rechts.</p>
        </div>
      )}
    </div>
  );
}
